<script>
  import { computed } from '@nuxtjs/composition-api'
  import { cartGetters, productGetters } from '@/composables/propeller/getters'
  import { QLoader } from '@qikkeronline/qikker-ui'
  import NumberInput from '@/components/NumberInput'
  import ProductStock from '@/components/Ecommerce/Products/ProductStock'
  import ProductPrice from '@/components/Ecommerce/Products/ProductPrice'
  import { useAttribute, useListAttribute } from '@/composables/useAttribute'
  import { isAvailable, isOrderable } from '@/utils/utils'
  import Badge from '@/components/Ecommerce/Badge'
  import { productAttributes } from '@/constants/products.module'

  export default {
    components: {
      QLoader,
      ProductPrice,
      ProductStock,
      NumberInput,
      Badge
    },
    props: {
      item: {
        type: Object,
        default: () => ({})
      },
      loading: {
        type: Boolean,
        default: false
      },
      discountInfo: {
        type: Object,
        default: () => {}
      }
    },
    setup (props) {
      const { getItemImage, getItemQty, getItemGrossListTotal } = cartGetters
      const { getStatus, getSlug, getShortName, getName } = productGetters
      const isExamenBuddy = computed(() => {
        const examenMerk = useAttribute(props.item.product, productAttributes.brand)
        return examenMerk.value === 'Examenbuddy' ?? false;
      });
      return {
        isOrderable: computed(() => isOrderable(props.item.product)),
        isAvailable: computed(() => isAvailable(props.item.product)),
        status: computed(() => getStatus(props.item.product)),
        productType: useAttribute(props.item.product, productAttributes.brand),
        levels: computed(() => useListAttribute(props.item.product, productAttributes.educationType).value.join(', ')),
        slug: computed(() => getSlug(props.item.product)),
        quantity: computed(() => getItemQty(props.item)),
        // "Was" price: the gross list total before the incentive. `priceNet` is
        // the ALREADY discounted unit price, so using it here made was and now
        // identical and the strikethrough never rendered.
        priceNet: computed(() => getItemGrossListTotal(props.item)),
        priceDiscount: computed(() => props.item.totalPriceNet),
        // The cart query returns no `shortNames`, so the line rendered without a
        // title. Same fallback ProductCard already uses.
        name: computed(() => getShortName(props.item.product) || getName(props.item.product)),
        image: computed(() => getItemImage(props.item)),
        // Propeller returns this percentage POSITIVE (verified live: 5 for the
        // 2-item stapelkorting). Negating it made the badge condition `> 0`
        // always false, so the badge never showed.
        discountPercentage: computed(() => Math.round(Math.abs(props.discountInfo?.percentage ?? 0))),
        isExamenBuddy
      }
    }
  }

</script>

<template>
  <div class="relative w-full py-6 border-b border-gray-200 md:py-4 cart-item">
    <transition name="fade">
      <div
        v-if="loading"
        class="absolute top-0 bottom-0 left-0 right-0 z-30 flex items-center justify-center bg-white bg-opacity-50 cursor-wait"
      >
        <QLoader
          :loading="loading"
          class="is-black"
        />
      </div>
    </transition>
    <div class="flex items-start transition-all duration-100 cart-item md:items-center">
      <img
        :src="image"
        :alt="name"
        class="w-20 md:w-30"
        :class="{ 'opacity-50': !isOrderable }"
      >
      <div class="flex flex-col justify-between w-full pl-4 md:flex-row md:gap-x-3 md:pl-4">
        <div class="w-64">
          <nuxt-link
            :to="`/producten/${item.productId}/${slug}`"
            class="transition-all hover:text-gray-600"
          >
            <h3
              class="mb-1 overflow-hidden text-lg leading-normal font-primary text-ellipsis"
              :title="name"
            >
              {{ name }}
            </h3>
          </nuxt-link>
          <div class="flex flex-row md:flex-col">
            <p class="text-base text-gray-600">
              {{ productType }}
            </p>
            <p class="text-base text-gray-600">
              {{ levels }}
            </p>
          </div>
        </div>
        <div class="w-full mt-4 md:mt-0">
          <ProductStock
            class="pr-5 lg:flex-col"
            :is-orderable="isOrderable"
            :is-available="isAvailable"
            :is-examen-buddy="isExamenBuddy"
          />
        </div>
        <div class="flex flex-col justify-between w-full pt-4 mt-4 border-t border-gray-200 md:flex-row md:items-start md:max-w-96 md:pt-0 md:mt-0 md:border-0">
          <div class="flex flex-col items-start pb-4 space-y-4 md:pb-0">
            <number-input
              :id="'quantity-' + item.itemId"
              :max="99"
              :value="quantity"
              @onChange="$emit('update', $event)"
            />
            <button
              type="button"
              class="items-center space-x-2 text-link is-secondary"
              @click="$emit('remove')"
            >
              <q-icon
                class="text-sm"
                name="trash"
              />
              <span>verwijderen</span>
            </button>
          </div>
          <div class="flex flex-col justify-end pt-4 border-t border-gray-200 md:border-t-0 md:pt-0 md:justify-start">
            <ProductPrice
              v-if="priceNet"
              :price="priceNet"
              class="mb-2 text-right"
              :class="{ 'text-gray-400': !isOrderable }"
              :discount="priceDiscount"
              :discount-percentage="discountPercentage"
              direction="col"
              show-decimals
            />
            <Badge v-if="discountPercentage && discountPercentage > 0">
              {{ discountPercentage }}% korting
            </Badge>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// should work out of the box in tailwind, but doesn't
.text-ellipsis {
  text-overflow: ellipsis;
}

.text-link.is-secondary {
  &::after {
    @apply bg-gray-300
  }
}
</style>
